export enum INTL_LOG_MODES {
  SILENT = 0,
  INFO = 1,
  ERROR = 2
}

export const CLIENT_SECRET_HASH_LENGTH = 64;

export const COPY_SUCCESS_TIMEOUT = 2000;

const getScalekitDocsUrl = (path: string) => `https://docs.scalekit.com${path}`;

const SEC_IN_MILLIS = 1000;
const MIN_IN_MILLIS = 60 * SEC_IN_MILLIS;
const HOUR_IN_MILLIS = 60 * MIN_IN_MILLIS;
const DAY_IN_MILLIS = 24 * HOUR_IN_MILLIS;
const MONTH_IN_MILLIS = 30 * DAY_IN_MILLIS;
const YEAR_IN_MILLIS = 365 * DAY_IN_MILLIS;
const CUSTOM_SAML_DOC_LINK = getScalekitDocsUrl(
  `/guides/integrations/sso-integrations/generic-saml/`
);
const CUSTOM_OIDC_DOC_LINK = getScalekitDocsUrl(
  '/guides/integrations/sso-integrations/generic-oidc/'
);

const CONSTANTS = {
  colors: { orange: 'orange-400', white: 'white' },
  devConfig: {
    intlLogMode: INTL_LOG_MODES.SILENT // For regular development, let it be set to INFO
  },
  externalLinks: {
    supportEmail: 'support',
    supportEmailDomain: 'scalekit.com',
    termsAndConditions: 'https://www.scalekit.com/legal/terms-of-service',
    privacyPolicy: 'https://www.scalekit.com/legal/privacy-policy',
    slackInviteLink:
      'https://join.slack.com/t/scalekit-community/shared_invite/zt-3gsxwr4hc-0tvhwT2b_qgVSIZQBQCWRw',
    oauthGuidePlaceholder: getScalekitDocsUrl('/'),
    websiteBookDemoUrl: 'https://www.scalekit.com/demo',
    ssoDocumentationSiteLink: getScalekitDocsUrl('/sso/quickstart'),
    scimDocumentationSiteLink: getScalekitDocsUrl('/scim/quickstart'),
    mcpAuthDocumentationSiteLink: getScalekitDocsUrl('/mcp/quickstart'),
    mcpClientTypesDocumentationSiteLink: getScalekitDocsUrl(
      '/mcp/managing-mcp-clients'
    ),
    agentActionsDocumentationSiteLink: getScalekitDocsUrl(
      '/agent-actions/quickstart'
    ),
    passwordlessDocumentationSiteLink: getScalekitDocsUrl(
      '/passwordless/quickstart'
    ),
    ssoSampleAppLink: 'https://github.com/scalekit-inc/sso-express-example',
    scimSampleAppLink:
      'https://github.com/scalekit-inc/nextjs-example-apps/tree/main/webhook-events',
    mcpAuthSampleAppLink: 'https://github.com/scalekit-inc/mcp-auth-demos',
    agentActionsSampleAppLink:
      'https://github.com/scalekit-inc/python-connect-demos',
    passwordlessSampleAppLink:
      'https://github.com/scalekit-developers/blogops-app-examples/tree/main/express-passwordless-auth',
    idpInitatedSsoDocLink: getScalekitDocsUrl('/sso/guides/idp-init-sso/'),
    redirectUriDocLink: getScalekitDocsUrl('/guides/dashboard/redirects/'),
    listUsersAndGroupsDocLink: getScalekitDocsUrl(
      '/directory/scim/quickstart/#retrieving-a-directory'
    ),
    testYourIntegrationDocLink: getScalekitDocsUrl('/guides/integrations/'),
    kbArticles: {
      root: getScalekitDocsUrl(''),
      environmentDetails: getScalekitDocsUrl(''),
      clientSecret: getScalekitDocsUrl('/guides/client-credentials-practices/'),
      redirectUris: getScalekitDocsUrl('/guides/dashboard/redirects'),
      allowedCallbackUrls: getScalekitDocsUrl(
        '/guides/dashboard/redirects/#allowed-callback-urls'
      ),
      postLogoutUrls: getScalekitDocsUrl(
        '/guides/dashboard/redirects/#post-logout-url'
      ),
      initiateLoginUrl: getScalekitDocsUrl(
        '/guides/dashboard/redirects/#initiate-login-url'
      ),
      backChannelLogoutUrls: getScalekitDocsUrl(
        '/guides/dashboard/redirects/#back-channel-logout-url'
      ),
      domainAddAndVerify: '',
      customerPortalHelpLink: '',
      mockIdpIntegrationGuide: getScalekitDocsUrl('/sso/guides/test-sso/'),
      singleSignonQuickStartGuide: getScalekitDocsUrl(
        '/sso/in-product-quickstart' // TODO: Remove, since irrelevant today
      ),
      directorySyncQuickStartGuide: getScalekitDocsUrl(
        '/scim/in-product-quickstart' // TODO: Remove, since irrelevant today
      ),
      enterpriseSsoOverview: getScalekitDocsUrl('/sso/guides/sso-basics/'),
      fsaQuickstartGuide: getScalekitDocsUrl('/authenticate/fsa/quickstart'),
      fsaMigrationGuide: getScalekitDocsUrl('/fsa/guides/migration-guide'),
      mcpCustomAuthGuide: getScalekitDocsUrl('/mcp/auth-methods/custom-auth'),
      interceptorsGuide: getScalekitDocsUrl('/guides/auth-flow-interceptors/'),
      idpSSODocLink: getScalekitDocsUrl(
        '/authenticate/sso/add-modular-sso/#handle-idp-initiated-sso'
      )
    },
    idpIntegrationDocLinks: {
      saml: {
        okta: getScalekitDocsUrl(
          '/guides/integrations/sso-integrations/okta-saml/'
        ),
        onelogin: getScalekitDocsUrl(
          '/guides/integrations/sso-integrations/onelogin-saml/'
        ),
        google: getScalekitDocsUrl(
          '/guides/integrations/sso-integrations/google-saml/'
        ),
        microsoftAd: getScalekitDocsUrl(
          '/guides/integrations/sso-integrations/azure-ad-saml/'
        ),
        pingIdentity: getScalekitDocsUrl(
          '/guides/integrations/sso-integrations/pingidentity-saml/'
        ),
        jumpcloud: getScalekitDocsUrl(
          '/guides/integrations/sso-integrations/jumpcloud-saml/'
        ),
        adfs: getScalekitDocsUrl(
          '/guides/integrations/sso-integrations/microsoft-ad-fs/'
        ),
        custom: CUSTOM_SAML_DOC_LINK
      },
      oidc: {
        okta: CUSTOM_OIDC_DOC_LINK,
        onelogin: CUSTOM_OIDC_DOC_LINK,
        google: CUSTOM_OIDC_DOC_LINK,
        microsoftAd: CUSTOM_OIDC_DOC_LINK,
        pingIdentity: CUSTOM_OIDC_DOC_LINK,
        jumpcloud: CUSTOM_OIDC_DOC_LINK,
        adfs: CUSTOM_OIDC_DOC_LINK,
        custom: CUSTOM_OIDC_DOC_LINK
      },
      scim: {
        okta: getScalekitDocsUrl(
          '/guides/integrations/scim-integrations/okta-scim/'
        ),
        onelogin: getScalekitDocsUrl(
          '/guides/integrations/scim-integrations/onelogin/'
        ),
        microsoftAd: getScalekitDocsUrl(
          '/guides/integrations/scim-integrations/azure-scim/'
        ),
        google: getScalekitDocsUrl(
          '/guides/integrations/scim-integrations/google-dir-sync/'
        ),
        jumpcloud: getScalekitDocsUrl(
          '/guides/integrations/scim-integrations/jumpcloud/'
        ),
        pingIdentity: getScalekitDocsUrl(''),
        custom: getScalekitDocsUrl('')
      }
    },
    socialConnectionGuideLinks: {
      home: getScalekitDocsUrl('/guides/integrations/'),
      google: getScalekitDocsUrl(
        '/guides/integrations/social-connections/google/'
      ),
      github: getScalekitDocsUrl(
        '/guides/integrations/social-connections/github/'
      ),
      gitlab: getScalekitDocsUrl(
        '/guides/integrations/social-connections/gitlab/'
      ),
      microsoft: getScalekitDocsUrl(
        '/guides/integrations/social-connections/microsoft/'
      ),
      salesforce: getScalekitDocsUrl(
        '/guides/integrations/social-connections/salesforce/'
      ),
      linkedin: getScalekitDocsUrl(
        '/guides/integrations/social-connections/linkedin/'
      )
    },
    promptSocialConnectionGuideLinks: {
      google: getScalekitDocsUrl(
        '/guides/integrations/social-connections/google/#configure-login-prompt-behavior'
      ),
      microsoft: getScalekitDocsUrl(
        '/guides/integrations/social-connections/microsoft/#choose-the-user-experience-for-login-prompt'
      )
    }
  },
  appConfig: {
    apiRetries: 2,
    maxSupportedClientSecrets: 2,
    maxSupportedRedirectUtis: 5
  },
  timeSignatures: {
    SEC_IN_MILLIS,
    MIN_IN_MILLIS,
    HOUR_IN_MILLIS,
    DAY_IN_MILLIS,
    MONTH_IN_MILLIS,
    YEAR_IN_MILLIS
  },
  localStorageKeys: {
    getEnvironmentSelection: () => `e_s_item`,
    getTestConnectionKey: (connectionId: string) => `t_c_r_${connectionId}`,
    getCodeBlockLanguageKey: () => `c_b_l_k`,
    getLastUsedLoginMethodKey: () => `sk.lulm`
  },
  MESSAGE_TYPES: { TEST_SSO_CONNCETION_RESULTS: 'TEST_SSO_CONNCETION_RESULTS' },
  QUERY_PARAMS: {
    CUSTOMER_PORTAL_AUTH_TOKEN: 'token',
    CUSTOMER_PORTAL_LINK_ID: 'link_id',
    ONBOARDING_PARAM_NAME: 'onboarding',
    POST_LOGOUT_REDIRECT_URI: 'post_logout_redirect_uri',
    SHOW_RESOURCE_METADATA_MODAL: 'show_resource_metadata_modal',
    TOOL_NAME: 'tool_name',
    ACTION: 'action',
    AUTHORIZATION_CODE: 'code',
    STATE: 'state',
    CLIENT_ID: 'client_id',
    PROMPT: 'prompt',
    IDP_INITIATED_LOGIN: 'idp_initiated_login',
    ERROR_ID: 'error_id',
    ERROR: 'error',
    ERROR_DESCRIPTION: 'error_description',
    SOURCE: 'source',
    ACTION_VALUES: {
      EDIT: 'edit'
    },
    CONNECTED_ACCOUNT_IDENTIFIER: 'ca_id'
  },
  supportEmail: 'support',
  supportEmailDomain: 'scalekit.com',
  samlAllowedCertificateExtensions: ['.pem', '.crt', '.cer', '.cert', '.key'],
  DEFAULT_TABLE_PAGE_SIZE: 20,
  CDN_HOST: {
    STAGING: 'cdn.scalekit.cloud',
    PRODUCTION: 'cdn.scalekit.com'
  }
};

const {
  colors: appColors,
  devConfig,
  externalLinks,
  appConfig,
  timeSignatures,
  ...miscConstants
} = CONSTANTS;

export {
  appColors,
  devConfig,
  externalLinks,
  appConfig,
  timeSignatures,
  miscConstants
};
